import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ConfigService } from './config.service';
import { sleep } from '@/utils/helpers';
import { LocalDataServiceService } from './local-data-service';

import { createUserWithEmailAndPassword } from '@firebase/auth';
import {
    User,
    onAuthStateChanged,
    signInWithEmailAndPassword,
    signInWithPopup
} from 'firebase/auth';
import { GoogleAuthProvider } from 'firebase/auth';
import { firebaseAuth } from '@/firebase';
import { FormGroup } from '@angular/forms';
import { Observable } from 'rxjs';

const provider = new GoogleAuthProvider();

@Injectable({
    providedIn: 'root'
})
export class AppService {
    public user?: User | null = null;

    constructor(
        private router: Router,
        private toastr: ToastrService,
        private http: HttpClient,
        private configService: ConfigService,
        private localDataService: LocalDataServiceService
    ) {
        onAuthStateChanged(
            firebaseAuth,
            (user) => {
                if (user) {
                    this.user = user;
                } else {
                    this.user = undefined;
                }
            },
            (e) => {
                this.user = undefined;
            }
        );
    }

    async registerWithEmail(email: string, password: string) {
        try {
            const result = await createUserWithEmailAndPassword(
                firebaseAuth,
                email,
                password
            );
            this.user = result.user;
            this.router.navigate(['/']);
            return result;
        } catch (error) {
            this.toastr.error(error.message);
        }
    }

    async loginWithEmail(email: string, password: string) {
        try {
            const result = await signInWithEmailAndPassword(
                firebaseAuth,
                email,
                password
            );
            this.user = result.user;
            this.router.navigate(['/']);

            return result;
        } catch (error) {
            this.toastr.error(error.message);
        }
    }

    async signInByGoogle() {
        try {
            const result = await signInWithPopup(firebaseAuth, provider);
            this.user = result.user;
            this.router.navigate(['/']);

            return result;
        } catch (error) {
            this.toastr.error(error.message);
        }
    }

    async getProfile() {
        await sleep(500);
        const user = firebaseAuth.currentUser;
        if (user) {
            this.user = user;
        } else {
            throw new Error('No user logged in');
        }
    }

    async logout() {
        localStorage.clear();
        this.router.navigate(['/login']);
    }

    loginUserData(login: FormGroup): Observable<any> {
        return this.http.post(
            this.configService.getConfig().END_POINT + "/auth/login", login);
    }

    // --- Dashboard Endpoints ---
    getDashboardStats(): Observable<any> {
        return this.http.get<any>(`${this.configService.getConfig().END_POINT}/api/dashboard/stats`, { headers: this.localDataService.getHeaders() });
    }

    // --- Product Endpoints ---
    getAllProducts(): Observable<any[]> {
        return this.http.get<any[]>(`${this.configService.getConfig().END_POINT}/api/product/get-all`, { headers: this.localDataService.getHeaders() });
    }

    getAvailableProducts(): Observable<any[]> {
        return this.http.get<any[]>(`${this.configService.getConfig().END_POINT}/api/product/getAvailableProducts`, { headers: this.localDataService.getHeaders() });
    }

    getProductById(productId: number): Observable<any> {
        return this.http.get<any>(`${this.configService.getConfig().END_POINT}/api/product/get-by-id/${productId}`, { headers: this.localDataService.getHeaders() });
    }

    getProductsByCategoryId(categoryId: number): Observable<any[]> {
        return this.http.get<any[]>(`${this.configService.getConfig().END_POINT}/api/product/get-by-category-id/${categoryId}`, { headers: this.localDataService.getHeaders() });
    }

    addProduct(product: any): Observable<any> {
        return this.http.post<any>(`${this.configService.getConfig().END_POINT}/api/product/add`, product, { headers: this.localDataService.getHeaders() });
    }

    updateProduct(product: any): Observable<any> {
        return this.http.put<any>(`${this.configService.getConfig().END_POINT}/api/product/update`, product, { headers: this.localDataService.getHeaders() });
    }

    updateProductAvailability(payload: any): Observable<any> {
        return this.http.put<any>(`${this.configService.getConfig().END_POINT}/api/product/update-availability`, payload, { headers: this.localDataService.getHeaders() });
    }

    deleteProduct(productId: number): Observable<any> {
        return this.http.delete<any>(`${this.configService.getConfig().END_POINT}/api/product/delete/${productId}`, { headers: this.localDataService.getHeaders() });
    }

    deleteProductImage(assetId: number): Observable<any> {
        return this.http.delete(`${this.configService.getConfig().END_POINT}/api/product/image-details/delete/${assetId}`, { headers: this.localDataService.getHeaders() });
    }

    getProductImageUrl(imageId: number): string {
        return `${this.configService.getConfig().END_POINT}/api/product/image-details/get/${imageId}`;
    }

    // --- Category Endpoints ---
    getAllCategories(): Observable<any[]> {
        return this.http.get<any[]>(`${this.configService.getConfig().END_POINT}/api/category/get-all`, { headers: this.localDataService.getHeaders() });
    }

    addCategories(categories: any[]): Observable<any> {
        return this.http.post<any>(`${this.configService.getConfig().END_POINT}/api/category/bulk-insert`, categories, { headers: this.localDataService.getHeaders() });
    }

    updateCategory(category: any): Observable<any> {
        return this.http.post<any>(`${this.configService.getConfig().END_POINT}/api/category/update`, category, { headers: this.localDataService.getHeaders() });
    }

    deleteCategory(categoryId: number): Observable<any> {
        return this.http.delete<any>(`${this.configService.getConfig().END_POINT}/api/category/delete/${categoryId}`, { headers: this.localDataService.getHeaders() });
    }

    // --- Specification Endpoints ---
    getAllSpecifications(): Observable<any[]> {
        return this.http.get<any[]>(`${this.configService.getConfig().END_POINT}/api/product/getAll-specifications`, { headers: this.localDataService.getHeaders() });
    }

    deleteProductSpecification(id: number): Observable<any> {
        return this.http.delete<any>(`${this.configService.getConfig().END_POINT}/api/product/specification-value/delete/${id}`, { headers: this.localDataService.getHeaders() });
    }

    // --- Enquiry Endpoints ---
    sendEnquiry(enquiry: any): Observable<any> {
        return this.http.post<any>(`${this.configService.getConfig().END_POINT}/api/enquiry/send`, enquiry);
    }

    getAllEnquiries(): Observable<any[]> {
        return this.http.get<any[]>(`${this.configService.getConfig().END_POINT}/api/enquiry/get-all`, { headers: this.localDataService.getHeaders() });
    }

    updateEnquiryStatus(payload: { enquiryId: number, status: string }): Observable<any> {
        return this.http.put<any>(`${this.configService.getConfig().END_POINT}/api/enquiry/update-status`, payload, { headers: this.localDataService.getHeaders() });
    }
}
